import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Autocomplete,
  Checkbox,
  Paper,
  Typography,
  Chip,
  Grid,
  InputAdornment,
  CircularProgress,
  Divider
} from '@mui/material';
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { get, post } from 'src/services/httpClient.service';
import { authenticationService } from "src/services";
import { useSnackbar } from 'src/components/snackbar/useSnackbar';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CategoryIcon from '@mui/icons-material/Category';
import StreamIcon from '@mui/icons-material/Stream';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';

interface ErrorMessages {
  name: string;
  appType: string;
  streamId: string;
}

interface Stream {
  id: string;
  name: string;
  url: string;
}

const EditCategory = ({
  handleClose,
  apiURL,
  category
}) => {
  const { showSnackbar } = useSnackbar();
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
  
  const [categoryData, setCategoryData] = useState({
    name: '',
    appType: 'Live',
    streamId: '',
    logo: null,
    imageUpload: null,
    landscapeLogo: null,
  });

  const [loadingStreams, setLoadingStreams] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [streamIps, setStreamIps] = useState<Stream[]>([]);
  const [existingStreams, setExistingStreams] = useState<Stream[]>([]);
  const [newStreams, setNewStreams] = useState<Stream[]>([]);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({
    name: "",
    appType: "",
    streamId: "",
  });

  const [preview, setPreview] = useState({
    logo: null,
    imageUpload: null,
    landscapeLogo: null,
  });

  const APP_TYPES = [
    { label: 'Live', value: 'Live' },
    { label: 'VOD', value: 'VOD' }
  ];

  useEffect(() => {
    if (!category) return;

    setCategoryData({
      name: category.name || "",
      appType: category.appType || "Live",
      streamId: "",
      logo: null,
      imageUpload: null,
      landscapeLogo: null
    });
  }, [category]);

  useEffect(() => {
    fetchServerIps();
  }, [vendorId]);

  const fetchServerIps = async () => {
    setLoadingStreams(true);
    try {
      const response = await get(`${process.env.REACT_APP_BASE_API_URL}/api/vendor-streams/vendor/${vendorId}/findAll`);
      if (Array.isArray(response?.data)) {
        const filteredStreams = response?.data.filter(
          (stream) => stream.serverType === "STREAM"
        );
        setStreamIps(filteredStreams);
      }
    } catch (error) {
      console.error('Error fetching stream IPs:', error);
    } finally {
      setLoadingStreams(false);
    }
  };

  useEffect(() => {
    if (category && streamIps.length) {
      const ids = Array.isArray(category.streamIds)
        ? category.streamIds
        : (category.streamIds || "")
          .replace("[", "")
          .replace("]", "")
          .split(",")
          .map(id => id.trim())
          .filter(Boolean);

      const existing = streamIps.filter(s => ids.includes(s.id));
      setExistingStreams(existing);
    }
  }, [category, streamIps]);

  const availableStreams = streamIps.filter(stream =>
    !existingStreams.some(e => e.id === stream.id)
  );

  const handleChange = (event, field) => {
    let { value, type, checked } = event.target;

    if (type !== 'checkbox') {
      value = value.replace(/^\s+/, '');
    }

    setCategoryData((prevData) => ({
      ...prevData,
      [field]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = () => {
    let errors: ErrorMessages = {
      name: "",
      appType: "",
      streamId: "",
    };

    if (!categoryData.name.trim()) {
      errors.name = "Category Name is required";
    }
    if (!categoryData.appType.trim()) {
      errors.appType = "Category Type is required";
    }
    if (newStreams.length === 0) {
      errors.streamId = "Please select at least one stream";
    }

    setErrorMessages(errors);
    return Object.values(errors).every((msg) => msg === "");
  };

  const handleSubmit = async () => {
   /*  if (!validateForm()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    } */

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("id", String(category.id));
      formData.append("name", categoryData.name.trim());
      formData.append("appType", categoryData.appType.trim());
      formData.append("vendorId", vendorId);

      const finalStreams = [...existingStreams, ...newStreams];
      finalStreams.forEach(stream => {
        formData.append("streamIds", stream.id);
      });

      const response = await post(`${apiURL}/categories/update`, formData);

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        showSnackbar(response?.message, "success");
        handleClose();
      }
    } catch (error) {
      if (error?.response?.data?.data != undefined && error?.response?.data?.data != "") {
        showSnackbar(error?.response?.data?.data, 'error');
      } else {
        showSnackbar(error?.message, 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleRemoveStream = (streamId) => {
    setExistingStreams(prev => prev.filter(s => s.id !== streamId));
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Category Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={categoryData.name}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root.Mui-disabled': {
                      backgroundColor: '#000000',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="App Type"
                  size="small"
                  fullWidth
                  value={categoryData.appType}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CategoryIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root.Mui-disabled': {
                      backgroundColor: '#000000',
                    },
                  }}
                >
                  {APP_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Current Streams */}
          {existingStreams.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
                Current Streams
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {existingStreams.map(stream => (
                  <Chip
                    key={stream.id}
                    icon={<StreamIcon />}
                    label={stream.name}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Add More Streams */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
              Add More Streams
            </Typography>
            <Autocomplete
              multiple
              options={availableStreams}
              value={newStreams}
              onChange={(e, value) => {
                setNewStreams(value);
              }}
              getOptionLabel={(option) => option.name}
              loading={loadingStreams}
              loadingText="Loading streams..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search streams..."
                  error={!!errorMessages.streamId}
                  helperText={errorMessages.streamId}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <StreamIcon color="action" fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loadingStreams && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    {option.url && (
                      <Typography variant="caption" color="textSecondary">
                        {option.url}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    icon={<StreamIcon />}
                    label={option.name}
                    {...getTagProps({ index })}
                    color="secondary"
                    size="small"
                  />
                ))
              }
            />
            {newStreams.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="caption" color="textSecondary">
                  Selected: {newStreams.length} stream(s)
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Actions */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={submitting}
            size="medium"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="contained"
            color="primary"
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {submitting ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditCategory;